import * as React from 'react'
import { useNavigate, useParams } from 'react-router'
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import type { ConsentOptionsDto, McpAppDto } from '@lib/dto/mcp.dto'
import { uuidParamSchema, type Uuid } from '@lib/validators/params'
import { useAuth } from '@/auth/AuthContext'
import { useTenantAuth } from '@/auth/TenantAuthContext'
import { AuthShell } from '@/components/AuthShell'
import { AppIdentity } from '@/components/connect/AppIdentity'
import { MemberConsentForm } from '@/components/connect/MemberConsentForm'
import { StaffConsentForm } from '@/components/connect/StaffConsentForm'
import { ErrorState, LoadingState } from '@/components/PageState'
import { Button } from '@/components/ui/button'
import { SegmentedControl, type SegmentedOption } from '@/components/ui/segmented-control'
import { isApiError } from '@/helpers/apiClient'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { mcpConsentKeys } from '@/hooks/queryKeys'
import { memberConsentService, publicConsentService, staffConsentService } from '@/services/McpConsentService'

type ConsentAs = ConsentOptionsDto['as']

const CONNECT_AS: SegmentedOption<ConsentAs>[] = [
  { value: 'tenant_user', label: 'Organization member' },
  { value: 'staff', label: 'PixelSign staff' },
]

/** The request expired, already finished, or never existed: only the AI app can start a new one. */
function isGone<E>(error: E): boolean {
  return isApiError(error) && (error.status === 404 || error.body.code === 'mcp.consent_invalid')
}

/** Who is connecting: the only session there is, or the one chosen when signed in as both. */
function signedInAs(member: boolean, staff: boolean, chosen: ConsentAs): ConsentAs | null {
  if (member && staff) return chosen
  if (member) return 'tenant_user'
  if (staff) return 'staff'
  return null
}

function serviceFor(as: ConsentAs) {
  return as === 'staff' ? staffConsentService : memberConsentService
}

function FullPageLoading() {
  return (
    <div className="flex min-h-svh items-center justify-center bg-canvas">
      <LoadingState />
    </div>
  )
}

function ExpiredRequest() {
  return (
    <AuthShell title="This connection request has expired" description="Start connecting again from your AI app.">
      <p className="text-body text-muted-foreground">You can close this tab.</p>
    </AuthShell>
  )
}

/**
 * Where an AI app connecting over MCP sends the person: sign in as an
 * organization member or as PixelSign staff, choose what the app may reach,
 * and go back to the app with the answer.
 */
export function ConnectPage() {
  const { requestId: rawRequestId } = useParams()
  const parsedRequestId = uuidParamSchema.safeParse(rawRequestId)
  const requestId = parsedRequestId.success ? parsedRequestId.data : null
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const tenant = useTenantAuth()
  const staff = useAuth()
  const { logout: tenantLogout } = tenant
  const { logout: staffLogout } = staff
  const [chosen, setChosen] = React.useState<ConsentAs>('tenant_user')
  const [returningTo, setReturningTo] = React.useState<string | null>(null)

  const appQuery = useQuery({
    queryKey: mcpConsentKeys.app(requestId),
    queryFn: () => {
      if (requestId === null) throw new Error('Connection link is not valid')
      return publicConsentService.app(requestId)
    },
    enabled: requestId !== null,
    retry: false,
  })
  const both = tenant.isAuthenticated && staff.isAuthenticated
  const as = signedInAs(tenant.isAuthenticated, staff.isAuthenticated, chosen)
  const optionsQuery = useQuery({
    // `as` is null only while this query is disabled. The query function is not
    // called then, and an in-flight query keeps the principal from the render
    // that started it. The fallback stays so this function typechecks.
    queryKey: mcpConsentKeys.options(requestId, as ?? 'tenant_user'),
    queryFn: () => {
      if (requestId === null) throw new Error('Connection link is not valid')
      return serviceFor(as ?? 'tenant_user').options(requestId)
    },
    enabled: requestId !== null && as !== null && appQuery.isSuccess,
    retry: false,
  })

  // A 401 from this visit means the session is dead. A 401 still cached from
  // before sign-in must not log out the session that just signed in: that
  // logout revokes it on the server, including on other devices.
  const sessionRejected =
    as !== null &&
    isApiError(optionsQuery.error) &&
    optionsQuery.error.status === 401 &&
    optionsQuery.isFetchedAfterMount &&
    !optionsQuery.isFetching
  React.useEffect(() => {
    if (!sessionRejected || requestId === null) return
    queryClient.removeQueries({ queryKey: mcpConsentKeys.options(requestId, as) })
    // Consent is a member or staff. This runs only after a rejected session,
    // which requires `as` to be one of those.
    if (as === 'staff') staffLogout()
    else tenantLogout()
  }, [sessionRejected, as, requestId, staffLogout, tenantLogout, queryClient])

  function signOut(): void {
    if (as === 'staff') staffLogout()
    else if (as === 'tenant_user') tenantLogout()
  }

  function returnToApp(app: McpAppDto, redirectUrl: string): void {
    setReturningTo(app.name)
    window.location.assign(redirectUrl)
  }

  if (requestId === null) {
    return (
      <AuthShell title="Connect an AI app">
        <ErrorState message="That connection link is not valid." />
      </AuthShell>
    )
  }

  if (returningTo !== null) {
    return (
      <AuthShell title={`Returning to ${returningTo}…`} description="You can close this tab if it doesn’t go back on its own.">
        <div className="flex justify-center py-4 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      </AuthShell>
    )
  }
  if (appQuery.isLoading || tenant.isLoading || staff.isLoading) return <FullPageLoading />
  if (appQuery.isError || !appQuery.data) {
    if (isGone(appQuery.error)) return <ExpiredRequest />
    return (
      <AuthShell title="Connect an AI app">
        <ErrorState
          message={apiErrorMessage(appQuery.error, 'Could not load this connection request.')}
          action={
            <Button variant="secondary" size="sm" onClick={() => void appQuery.refetch()}>
              Try again
            </Button>
          }
        />
      </AuthShell>
    )
  }

  const { app } = appQuery.data

  if (as === null) {
    const from = `/connect/${requestId}`
    return (
      <AuthShell title="Connect an AI app" size="md">
        <div className="space-y-4">
          <AppIdentity app={app} />
          <p className="text-body">Sign in to choose what it can access.</p>
          <div className="flex flex-col items-start gap-2">
            <Button onClick={() => void navigate('/login', { state: { from } })}>
              Sign in to your organization
            </Button>
            <Button variant="link" size="sm" onClick={() => void navigate('/ops/login', { state: { from } })}>
              PixelSign staff? Sign in here
            </Button>
          </div>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell title="Connect an AI app" description="Choose what it can reach in PixelSign." size="md">
      <div className="space-y-4">
        {both ? (
          <div className="space-y-1">
            <p className="text-caption font-medium text-muted-foreground">Connect as</p>
            <SegmentedControl ariaLabel="Connect as" value={chosen} onChange={setChosen} options={CONNECT_AS} fullWidth />
          </div>
        ) : null}
        <ConsentBody
          requestId={requestId}
          query={optionsQuery}
          onSignOut={signOut}
          onDone={(redirectUrl) => returnToApp(app, redirectUrl)}
        />
      </div>
    </AuthShell>
  )
}

interface ConsentBodyProps {
  requestId: Uuid
  query: UseQueryResult<ConsentOptionsDto>
  onSignOut: () => void
  onDone: (redirectUrl: string) => void
}

/** The form for whoever is connecting, once their options have loaded. */
function ConsentBody({ requestId, query, onSignOut, onDone }: ConsentBodyProps) {
  if (query.isLoading) return <LoadingState />
  if (query.error || !query.data) {
    if (isGone(query.error)) {
      return <ErrorState message="This connection request has expired. Start connecting again from your AI app." />
    }
    return (
      <ErrorState
        message={apiErrorMessage(query.error, 'Could not load your options.')}
        action={
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        }
      />
    )
  }
  const options = query.data
  if (options.as === 'staff') {
    return (
      <StaffConsentForm
        key={`staff-${options.user.email}`}
        requestId={requestId}
        options={options}
        service={staffConsentService}
        onSignOut={onSignOut}
        onDone={onDone}
      />
    )
  }
  return (
    <MemberConsentForm
      key={`member-${options.user.email}`}
      requestId={requestId}
      options={options}
      service={memberConsentService}
      onSignOut={onSignOut}
      onDone={onDone}
    />
  )
}
